import { useTranslation } from 'react-i18next';
import { getFreelancerSubtitle } from '../../utils/freelancerUtils';
import { formatDateLocalized } from '../../utils/dateUtils';

interface JobPreviewProps {
  job: {
    title: string;
    requiredServices: {
      slug: string;
    }[];
    city: {
      name: string;
    };
    onSite: boolean;
    description: string;
    languages: string[];
    budgetRange: {
      code: string;
    };
    startDate: string;
    endDate: string | null;
  };
  publication?: {
    expiresAt: string;
    selectionDate: string | null;
    listingType: string | null;
  };
}

const JobPreview = ({ job, publication }: JobPreviewProps) => {
  const { t, i18n } = useTranslation(['jobs', 'common']);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        {/* Left Column */}
        <div className="flex flex-col gap-4 md:basis-1/2">
          <div className="flex flex-col">
            <span className="text-sm text-secondary">{t('form.project_name')}</span>
            <span className="font-bold text-secondary">{job.title}</span>
          </div>
          {publication?.listingType && (
            <div className="flex flex-col">
              <span className="text-sm text-secondary">{t('publish.priority_title')}</span>
              <span className="font-bold text-secondary">{publication.listingType}</span>
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-sm text-secondary">{t('service_type')}</span>
            <span className="font-bold text-secondary">
              {getFreelancerSubtitle(
                { service_category_slugs: job.requiredServices.map(service => service.slug) },
                t
              )}
            </span>
          </div>
          {publication?.expiresAt && (
            <div className="flex flex-col">
              <span className="text-sm text-secondary">{t('expires_at')}</span>
              <span className="font-bold text-secondary">
                {formatDateLocalized(publication.expiresAt, i18n.language)}
              </span>
            </div>
          )}
          {publication?.selectionDate && (
            <div className="flex flex-col">
              <span className="text-sm text-secondary">{t('selection_date')}</span>
              <span className="font-bold text-secondary">
                {formatDateLocalized(publication.selectionDate, i18n.language)}
              </span>
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-sm text-secondary">{t('form.location')}</span>
            <span className="font-bold text-secondary">{job.city.name}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm text-secondary">{t('form.onsite')}</span>
            <span className="font-bold text-secondary">
              {job.onSite ? t('common:form_fields.yes') : t('common:form_fields.no')}
            </span>
          </div>
        </div>
        {/* Right Column */}
        <div className="flex flex-col gap-4 md:basis-1/2">
          <div className="flex flex-col">
            <span className="text-sm text-secondary">{t('form.languages')}</span>
            <span className="font-bold text-secondary">
              {job.languages?.map((l: string) => t(`language.${l}`, { ns: 'common' })).join(', ')}
            </span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm text-secondary">{t('form.price_range')}</span>
            <span className="font-bold text-secondary">
              {t(`budget_ranges.${job.budgetRange.code}`, { ns: 'common' })}
            </span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm text-secondary">{t('form.start_date')}</span>
            <span className="font-bold text-secondary">
              {job.startDate && formatDateLocalized(job.startDate, i18n.language)}
            </span>
          </div>

          <div className="flex flex-col">
            <span className="text-sm text-secondary">{t('form.end_date')}</span>
            <span className="font-bold text-secondary">
              {job.endDate && formatDateLocalized(job.endDate, i18n.language)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        <span className="text-sm text-secondary">{t('form.description')}</span>
        <span className="font-bold text-secondary whitespace-pre-line">{job.description}</span>
      </div>
    </div>
  );
};

export default JobPreview;
